import React, { useState } from 'react';
import { useFitStore } from '../store/useFitStore';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import { makePlannedMeal, type PlannedMeal } from '../lib/plannedMeals';

interface FoodProduct {
  product_name?: string;
  brands?: string;
  nutriments?: {
    'energy-kcal_100g'?: number;
    proteins_100g?: number;
    carbohydrates_100g?: number;
    fat_100g?: number;
  };
}

interface MacroBarProps {
  label: string;
  current: number;
  goal: number;
  color: string;
}

const MacroBar: React.FC<MacroBarProps> = ({ label, current, goal, color }) => (
  <div style={{ marginBottom: 'var(--spacing-md)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
      <span className="text-muted">{label.toUpperCase()}</span>
      <span>{current}g / {goal}g</span>
    </div>
    <div style={{ height: '6px', background: 'var(--bg-tertiary)', borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{ height: '100%', background: color, width: `${Math.min((current / goal) * 100, 100)}%`, transition: 'width 0.5s ease' }} />
    </div>
  </div>
);

const Nutrition: React.FC = () => {
  const { user, nutritionLogs, logMeal, showToast } = useFitStore();
  const today = new Date().toISOString().split('T')[0];
  const todayLog = nutritionLogs.find(log => log.date === today);
  
  const goals = user?.macroGoals || { calories: 2500, protein: 180, carbs: 250, fat: 70 };
  
  const totals = (todayLog?.meals || []).reduce((acc, meal) => ({
    calories: acc.calories + meal.calories,
    protein: acc.protein + meal.protein,
    carbs: acc.carbs + meal.carbs,
    fat: acc.fat + meal.fat
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  const [showAdd, setShowAdd] = useState(false);
  const [mealName, setMealName] = useState('');
  const [cals, setCals] = useState('');
  const [prot, setProt] = useState('');
  const [carb, setCarb] = useState('');
  const [fat, setFat] = useState('');

  /* ---------- food facts (Open Food Facts — free, no key) ----------
     Search a real food database and prefill macros per 100g. */
  interface FoodHit { name: string; brand?: string; kcal: number; protein: number; carbs: number; fatG: number }
  const [foodQ, setFoodQ] = useState('');
  const [foodHits, setFoodHits] = useState<FoodHit[]>([]);
  const [foodBusy, setFoodBusy] = useState(false);
  const searchFood = async () => {
    const q = foodQ.trim();
    if (!q || foodBusy) return;
    setFoodBusy(true);
    setFoodHits([]);
    try {
      const url = `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(q)}&search_simple=1&action=process&json=1&page_size=6&fields=product_name,brands,nutriments`;
      const res = await fetch(url);
      const data: { products?: FoodProduct[] } = await res.json();
      const hits: FoodHit[] = (data.products || [])
        .map((pr) => ({
          name: pr.product_name?.trim() || '',
          brand: pr.brands?.split(',')[0]?.trim() || undefined,
          kcal: Math.round(pr.nutriments?.['energy-kcal_100g'] ?? 0),
          protein: Math.round(pr.nutriments?.proteins_100g ?? 0),
          carbs: Math.round(pr.nutriments?.carbohydrates_100g ?? 0),
          fatG: Math.round(pr.nutriments?.fat_100g ?? 0),
        }))
        .filter((h: FoodHit) => h.name && h.kcal > 0);
      setFoodHits(hits);
      if (hits.length === 0) showToast('No matches — try a simpler name', 'error');
    } catch {
      showToast("Food database didn't respond — enter macros manually", 'error');
    }
    setFoodBusy(false);
  };
  const pickFood = (h: FoodHit) => {
    setMealName(h.brand ? `${h.name} (${h.brand})` : h.name);
    setCals(String(h.kcal));
    setProt(String(h.protein));
    setCarb(String(h.carbs));
    setFat(String(h.fatG));
    setFoodHits([]);
  };

  // A coach's plan should be usable, not a static inspiration board. Each item
  // carries the complete macro shape used by the real daily log so it can be
  // added in one tap.
  const [activePlannerDay, setActivePlannerDay] = useState<'DAY1' | 'DAY2' | 'DAY3'>('DAY1');
  type PlannerMeal = PlannedMeal & { type: string; img: string };
  const plannerMeals: Record<'DAY1' | 'DAY2' | 'DAY3', PlannerMeal[]> = {
    DAY1: [
      { name: 'Ahi Tuna with Soy Sauce', type: 'Breakfast', calories: 485, protein: 41, carbs: 28, fat: 20, img: 'https://picsum.photos/id/292/400/160' },
      { name: 'Apple and Peanut Butter', type: 'Snack', calories: 205, protein: 5, carbs: 31, fat: 9, img: 'https://picsum.photos/id/312/400/160' },
      { name: 'Grilled Chicken Bowl', type: 'Lunch', calories: 620, protein: 48, carbs: 62, fat: 18, img: 'https://picsum.photos/id/1060/400/160' }
    ],
    DAY2: [
      { name: 'Greek Yogurt Parfait', type: 'Breakfast', calories: 320, protein: 27, carbs: 40, fat: 6, img: 'https://picsum.photos/id/312/400/160' },
      { name: 'Salmon & Quinoa', type: 'Dinner', calories: 580, protein: 42, carbs: 51, fat: 22, img: 'https://picsum.photos/id/1060/400/160' }
    ],
    DAY3: [
      { name: 'Avocado Toast & Eggs', type: 'Breakfast', calories: 410, protein: 21, carbs: 35, fat: 22, img: 'https://picsum.photos/id/292/400/160' },
      { name: 'Turkey Wrap', type: 'Lunch', calories: 450, protein: 36, carbs: 44, fat: 14, img: 'https://picsum.photos/id/1060/400/160' }
    ]
  };

  const logPlannedMeal = async (meal: PlannerMeal) => {
    try {
      await logMeal(makePlannedMeal(meal));
      showToast(`${meal.name} added to today's log`);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Could not add that meal', 'error');
    }
  };

  const handleLogMeal = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await logMeal({
        name: mealName,
        calories: parseInt(cals) || 0,
        protein: parseInt(prot) || 0,
        carbs: parseInt(carb) || 0,
        fat: parseInt(fat) || 0
      });
      showToast(`${mealName} logged!`);
      setShowAdd(false);
      setMealName(''); setCals(''); setProt(''); setCarb(''); setFat('');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Could not log that meal', 'error');
    }
  };

  return (
    <div className="main-content" style={{ background: 'var(--bg-primary)' }}>
      <header className="mb-8">
        <h1 className="hero text-white text-[36px] leading-none font-bold tracking-[-0.025em]">Nutrition.</h1>
        <p className="text-white text-lg font-semibold tracking-tight mt-1">Fuel the mission.</p>
      </header>

      {/* SMART MEAL PLANNER - matching MyFitnessPal look (Image 2) */}
      <div style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-sm)' }}>Smart Meal Planner</h2>
        <div className="planner-tabs">
          {(['DAY1','DAY2','DAY3'] as const).map(d => (
            <div 
              key={d} 
              className={`planner-tab ${activePlannerDay === d ? 'active' : ''}`}
              onClick={() => setActivePlannerDay(d)}
            >
              {d.replace('DAY','DAY ')}
            </div>
          ))}
        </div>
        {plannerMeals[activePlannerDay].map((meal) => (
          <button
            key={meal.name}
            type="button"
            className="meal-card w-full text-left"
            onClick={() => void logPlannedMeal(meal)}
            aria-label={`Add ${meal.name} to today's meal log`}
            title="Add to today's log"
          >
            <img src={meal.img} alt="" />
            <div className="details">
              <h4>{meal.name}</h4>
              <div className="meta">{meal.type} · {meal.calories} Cal · {meal.protein}P / {meal.carbs}C / {meal.fat}F</div>
            </div>
            <span className="text-fit-text-secondary text-xs font-semibold shrink-0">+ Log</span>
          </button>
        ))}
        <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center' }}>Tap a planned meal to add it to today’s log.</p>
      </div>

      {/* CALORIE HUB */}
      <Card variant="glass" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase' }}>Calories Remaining</p>
          <h2 style={{ fontSize: '3.5rem', fontWeight: 900, margin: '8px 0', color: totals.calories > goals.calories ? 'var(--status-red)' : 'white' }}>
            {goals.calories - totals.calories}
          </h2>
          <p style={{ fontSize: '0.9rem', fontWeight: 600 }}>of {goals.calories} kcal</p>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: 'var(--bg-tertiary)' }}>
          <div style={{ height: '100%', background: 'var(--accent-primary)', width: `${Math.min((totals.calories / goals.calories) * 100, 100)}%` }}></div>
        </div>
      </Card>

      {/* MACRO BREAKDOWN */}
      <Card style={{ marginBottom: 'var(--spacing-xl)' }}>
        <MacroBar label="Protein" current={totals.protein} goal={goals.protein} color="var(--accent-primary)" />
        <MacroBar label="Carbs" current={totals.carbs} goal={goals.carbs} color="var(--accent-secondary)" />
        <MacroBar label="Fat" current={totals.fat} goal={goals.fat} color="#fcd34d" />
      </Card>

      {/* MEAL LOG */}
      <section style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
          <h2 style={{ fontSize: '1.1rem' }}>Today's Meals</h2>
          <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>+ Add Meal</Button>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
          {todayLog?.meals.map(meal => (
            <Card key={meal.id} variant="glass" padding="var(--spacing-md)" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ fontWeight: 700 }}>{meal.name}</p>
                <p className="text-muted" style={{ fontSize: '0.7rem' }}>{meal.protein}P • {meal.carbs}C • {meal.fat}F</p>
              </div>
              <p style={{ fontWeight: 800, fontSize: '1rem' }}>{meal.calories} kcal</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ADD MEAL MODAL */}
      {showAdd && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--spacing-lg)' }}>
          <Card style={{ width: '100%', maxWidth: '400px' }}>
            <h2 style={{ marginBottom: 'var(--spacing-lg)' }}>Log Meal</h2>
            {/* food-facts lookup — fills the form from a real database (per 100g) */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: 'var(--spacing-md)' }}>
              <Input
                label="Search food facts"
                value={foodQ}
                onChange={e => setFoodQ(e.target.value)}
                placeholder="e.g. greek yogurt"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchFood(); } }}
              />
              <div style={{ alignSelf: 'flex-end' }}>
                <Button variant="glass" size="sm" type="button" onClick={searchFood} disabled={foodBusy || !foodQ.trim()}>
                  {foodBusy ? '…' : 'Search'}
                </Button>
              </div>
            </div>
            {foodHits.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: 'var(--spacing-md)', maxHeight: 180, overflowY: 'auto' }}>
                {foodHits.map((h, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => pickFood(h)}
                    style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 8, background: 'var(--bg-tertiary)', fontSize: '0.8rem' }}
                  >
                    <span style={{ fontWeight: 700 }}>{h.name}</span>
                    {h.brand && <span style={{ color: 'var(--text-muted)' }}> · {h.brand}</span>}
                    <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                      per 100g: {h.kcal} kcal · {h.protein}P · {h.carbs}C · {h.fatG}F
                    </span>
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={handleLogMeal} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
              <Input label="Meal Name" value={mealName} onChange={e => setMealName(e.target.value)} required />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <Input label="Calories" type="number" value={cals} onChange={e => setCals(e.target.value)} required />
                <Input label="Protein (g)" type="number" value={prot} onChange={e => setProt(e.target.value)} required />
                <Input label="Carbs (g)" type="number" value={carb} onChange={e => setCarb(e.target.value)} required />
                <Input label="Fat (g)" type="number" value={fat} onChange={e => setFat(e.target.value)} required />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-md)' }}>
                <Button variant="glass" fullWidth type="button" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button variant="primary" fullWidth type="submit">Log it</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Nutrition;
